import { Button, Group, NumberInput, Select, Stack, Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { CONVERSION_FACTOR_DECIMALS } from './api';

interface UomOption {
  value: string;
  label: string;
}

interface Props {
  /** Code of the stocking unit — the left-hand side of the first row. */
  stockingCode: string;
  /** Code of the first conversion's unit — the left-hand side of the second row. */
  conversionCode: string;

  conversionUomId: string | null;
  conversionFactor: number | '';
  conversionUom2Id: string | null;
  conversionFactor2: number | '';

  conversionUomOptions: UomOption[];
  conversionUom2Options: UomOption[];

  errors: {
    conversionUomId?: string;
    conversionFactor?: string;
    conversionUom2Id?: string;
    conversionFactor2?: string;
  };

  disabled: boolean;
  /** No stocking unit chosen yet, so there is nothing to convert FROM. */
  noStockingUom: boolean;

  onChange: (next: {
    conversionUomId?: string | null;
    conversionFactor?: number | '';
    conversionUom2Id?: string | null;
    conversionFactor2?: number | '';
  }) => void;
}

/**
 * The two chained unit conversions, laid out as equations.
 *
 * Reading order is the whole point. This reads `1 RL = [300] [EA]` — row label, factor, unit —
 * which is how the sentence is spoken and how the current product presents it. The previous
 * version put the unit picker FIRST and buried the sentence in the number field's label ("1 CS
 * equals … EA"), so the eye met the answer before the question and the boss found it backwards.
 * The data model did not change; only the order in which it is asked for.
 *
 * The second row's left-hand side is the FIRST conversion's unit, not the stocking unit. That is
 * the genuinely confusing part of the model — stocking CASE with 1 CS = 1000 EA then 1 EA = 3 IN
 * means 3000 IN per case, not 3 — and showing "1 EA =" in the label is what makes it legible
 * without a paragraph of explanation.
 */
export function ItemConversionRows({
  stockingCode,
  conversionCode,
  conversionUomId,
  conversionFactor,
  conversionUom2Id,
  conversionFactor2,
  conversionUomOptions,
  conversionUom2Options,
  errors,
  disabled,
  noStockingUom,
  onChange,
}: Props) {
  const { t } = useTranslation();

  // Em dash until a unit is chosen, so the row still reads as an equation with a blank in it
  // rather than as "1  =".
  const fromFirst = stockingCode || '—';
  const fromSecond = conversionCode || '—';

  return (
    <Stack gap="xs">
      {/* Headers once, above the columns, rather than a label on every field. Repeating them per
          row is what made the old version feel like four unrelated inputs instead of two
          sentences. The inputs carry aria-labels so nothing is lost for a screen reader. */}
      <Group gap="sm" wrap="nowrap" pl={64}>
        <Text size="xs" fw={500} c="dimmed" w={150}>
          {t('items.conversionFactorHeader')}
        </Text>
        <Text size="xs" fw={500} c="dimmed" w={200}>
          {t('items.conversionUomHeader')}
        </Text>
      </Group>

      <Group gap="sm" wrap="nowrap" align="flex-start">
        <Text size="sm" fw={600} w={56} ta="right" mt={7} style={{ whiteSpace: 'nowrap' }}>
          {t('items.conversionRow', { unit: fromFirst })}
        </Text>
        <NumberInput
          w={150}
          value={conversionFactor}
          onChange={(v) => onChange({ conversionFactor: v === '' ? '' : Number(v) })}
          error={errors.conversionFactor}
          min={0}
          decimalScale={CONVERSION_FACTOR_DECIMALS}
          // Enabled only once there is a unit to convert to, mirroring the old behaviour: a factor
          // with no unit is the invalid state both the API and a CHECK constraint reject.
          disabled={disabled || !conversionUomId}
          aria-label={t('items.conversionFactorAria', { n: 1, unit: fromFirst })}
        />
        <Select
          w={200}
          data={conversionUomOptions}
          value={conversionUomId}
          onChange={(v) =>
            onChange(
              v
                ? { conversionUomId: v }
                : // Clearing the first conversion strands the second, which the API and the DB
                  // both reject, so take it with it rather than submit an invalid chain.
                  { conversionUomId: null, conversionFactor: '', conversionUom2Id: null, conversionFactor2: '' },
            )
          }
          error={errors.conversionUomId}
          placeholder={t('items.conversionUomNone')}
          searchable
          clearable
          nothingFoundMessage={t('shell.noMatch')}
          disabled={disabled || noStockingUom}
          aria-label={t('items.conversionUomAria', { n: 1, unit: fromFirst })}
        />
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconX size={14} />}
          disabled={disabled || (!conversionUomId && conversionFactor === '')}
          onClick={() =>
            onChange({
              conversionUomId: null,
              conversionFactor: '',
              conversionUom2Id: null,
              conversionFactor2: '',
            })
          }
          aria-label={t('items.conversionClearAria', { n: 1 })}
        >
          {t('items.conversionClear')}
        </Button>
      </Group>

      <Group gap="sm" wrap="nowrap" align="flex-start">
        <Text size="sm" fw={600} w={56} ta="right" mt={7} style={{ whiteSpace: 'nowrap' }}>
          {t('items.conversionRow', { unit: fromSecond })}
        </Text>
        <NumberInput
          w={150}
          value={conversionFactor2}
          onChange={(v) => onChange({ conversionFactor2: v === '' ? '' : Number(v) })}
          error={errors.conversionFactor2}
          min={0}
          decimalScale={CONVERSION_FACTOR_DECIMALS}
          disabled={disabled || !conversionUom2Id}
          aria-label={t('items.conversionFactorAria', { n: 2, unit: fromSecond })}
        />
        <Select
          w={200}
          data={conversionUom2Options}
          value={conversionUom2Id}
          onChange={(v) =>
            onChange(v ? { conversionUom2Id: v } : { conversionUom2Id: null, conversionFactor2: '' })
          }
          error={errors.conversionUom2Id}
          placeholder={t('items.conversionUomNone')}
          searchable
          clearable
          nothingFoundMessage={t('shell.noMatch')}
          // Nothing to chain from until the first conversion exists.
          disabled={disabled || !conversionUomId}
          aria-label={t('items.conversionUomAria', { n: 2, unit: fromSecond })}
        />
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconX size={14} />}
          disabled={disabled || (!conversionUom2Id && conversionFactor2 === '')}
          onClick={() => onChange({ conversionUom2Id: null, conversionFactor2: '' })}
          aria-label={t('items.conversionClearAria', { n: 2 })}
        >
          {t('items.conversionClear')}
        </Button>
      </Group>

      {/* Both dead states get a reason. Without a stocking unit there is nothing to convert FROM,
          so BOTH rows are disabled — and saying only "choose a first conversion" there would point
          at a control that is itself disabled. */}
      {noStockingUom ? (
        <Text size="xs" c="dimmed" pl={64}>
          {t('items.conversionNeedsStockingUomHint')}
        </Text>
      ) : (
        !conversionUomId && (
          <Text size="xs" c="dimmed" pl={64}>
            {t('items.conversionSecondNeedsFirstHint')}
          </Text>
        )
      )}
    </Stack>
  );
}
